import { useCallback, useEffect, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { api, bildLaden, fehlertext, type SessionUser } from "./api";
import { heute, sprache } from "./datum";
import { objektAuswahl } from "./objektname";
import { Auswahl, Eingabe, Fehler, Feld, Fotofeld, Knopf, Laedt, Leer, Seitenkopf } from "./ui";

/**
 * Material und Fremdkosten.
 *
 * Dieselbe Handbewegung wie im Tankbuch, weil es dieselbe Lage ist: jemand
 * steht mit einem Beleg in der Hand irgendwo, wo er nicht lange stehen
 * moechte. Objekt waehlen, Betrag abtippen, Beleg fotografieren, fertig.
 * Menge, Einheit und Lieferant sind freiwillig; wer sie zu Pflichtfeldern
 * macht, bekommt Einsen und Striche.
 *
 * **Material oder Fremdleistung** ist die einzige Pflichtentscheidung neben
 * Objekt und Betrag. Sie steht als zwei Knoepfe da und nicht als Auswahlliste,
 * denn es sind genau zwei, und eine Liste mit zwei Eintraegen ist ein Klick zu
 * viel.
 *
 * **Weiterberechenbar** ist standardmaessig an. Der haeufigere Fall ist die
 * Auslage fuer den Kunden, und was in der Pauschale steckt, weiss der, der
 * tippt, ohnehin besser als eine Vorgabe.
 */

interface Eintrag {
  id: string;
  objektId: string;
  objekt: string;
  kunde: string;
  abteilungId: string | null;
  abteilung: string | null;
  benutzerId: string;
  mitarbeiter: string;
  art: string;
  datum: string;
  bezeichnung: string;
  lieferant: string | null;
  menge: number | null;
  einheit: string | null;
  betragCent: number;
  weiterberechenbar: boolean;
  hatBeleg: boolean;
  notiz: string | null;
}

interface Liste {
  eintraege: Eintrag[];
  summeCent: number;
  summeMaterialCent: number;
  summeFremdCent: number;
}

interface Objekt {
  id: string;
  name: string;
  customer_name?: string;
}

interface Abteilung {
  id: string;
  name: string;
}

export function KostenSeite({ user }: { user: SessionUser }) {
  const { t } = useTranslation();
  const istAdmin = user.role === "admin";

  const [liste, setListe] = useState<Liste | null>(null);
  const [objekte, setObjekte] = useState<Objekt[]>([]);
  const [abteilungen, setAbteilungen] = useState<Abteilung[]>([]);
  const [monat, setMonat] = useState(() => new Date().toISOString().slice(0, 7));
  const [objektId, setObjektId] = useState("");
  const [fehler, setFehler] = useState<string | null>(null);
  const [neu, setNeu] = useState(false);

  const laden = useCallback(async () => {
    const [j, m] = monat.split("-").map(Number);
    const von = `${monat}-01`;
    // Der letzte Tag des Monats, nicht der erste des naechsten: die Abfrage
    // schliesst beide Enden ein.
    const bis = new Date(Date.UTC(j, m, 0)).toISOString().slice(0, 10);
    const filter = objektId ? `&objektId=${objektId}` : "";
    setListe(await api.get<Liste>(`/kosten?von=${von}&bis=${bis}${filter}`));
  }, [monat, objektId]);

  useEffect(() => {
    void laden().catch((e) => setFehler(fehlertext(e, t("fehler.laden"))));
  }, [laden, t]);

  useEffect(() => {
    void api.get<{ properties: Objekt[] }>("/properties")
      .then((a) => setObjekte(a.properties))
      .catch(() => setObjekte([]));
    void api.get<{ abteilungen: Abteilung[] }>("/abteilungen")
      .then((a) => setAbteilungen(a.abteilungen))
      .catch(() => setAbteilungen([]));
  }, []);

  return (
    <>
      <Seitenkopf
        titel={t("kosten.titel")}
        hinweis={t("kosten.hinweis")}
        aktionen={
          <Knopf disabled={objekte.length === 0} onClick={() => setNeu(true)}>
            {t("kosten.neuerEintrag")}
          </Knopf>
        }
      />
      <Fehler text={fehler} />

      <div className="mt-4 flex flex-wrap items-end gap-4">
        <Feld label={t("zeit.monat")}>
          <Eingabe type="month" value={monat} onChange={(e) => setMonat(e.target.value)} />
        </Feld>
        <Feld label={t("kosten.objekt")}>
          <Auswahl value={objektId} onChange={(e) => setObjektId(e.target.value)}>
            <option value="">{t("kosten.alleObjekte")}</option>
            {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
              <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
            ))}
          </Auswahl>
        </Feld>
        {liste && (
          <div className="flex flex-wrap gap-6 pb-2 text-sm">
            <Summe label={t("kosten.summe")} cent={liste.summeCent} gross />
            <Summe label={t("kosten.material")} cent={liste.summeMaterialCent} />
            <Summe label={t("kosten.fremd")} cent={liste.summeFremdCent} />
          </div>
        )}
      </div>

      {!liste ? <Laedt /> : liste.eintraege.length === 0 ? (
        <Leer text={t("kosten.leer")} />
      ) : (
        <ul className="mt-3 space-y-2">
          {liste.eintraege.map((e) => (
            <Zeile
              key={e.id}
              eintrag={e}
              zeigeName={istAdmin}
              darfLoeschen={istAdmin}
              onWeg={() => void laden()}
            />
          ))}
        </ul>
      )}

      {neu && (
        <EintragDialog
          objekte={objekte}
          abteilungen={abteilungen}
          onClose={() => setNeu(false)}
          onFertig={() => { setNeu(false); void laden(); }}
        />
      )}
    </>
  );
}

function Summe({ label, cent, gross = false }: { label: string; cent: number; gross?: boolean }) {
  return (
    <p>
      <span className="text-leise">{label}: </span>
      <span className={gross ? "text-lg font-bold tabular-nums" : "font-semibold tabular-nums"}>
        {euro(cent)}
      </span>
    </p>
  );
}

function Zeile({
  eintrag, zeigeName, darfLoeschen, onWeg
}: {
  eintrag: Eintrag;
  zeigeName: boolean;
  darfLoeschen: boolean;
  onWeg: () => void;
}) {
  const { t } = useTranslation();
  const [beleg, setBeleg] = useState<string | null>(null);
  const [fragt, setFragt] = useState(false);

  return (
    <li className="rounded-card bg-white p-4">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-semibold tabular-nums">{datum(eintrag.datum)}</span>
        <span className="font-semibold">{eintrag.bezeichnung}</span>
        <span className="rounded bg-grund px-2 py-0.5 text-xs">
          {t(eintrag.art === "material" ? "kosten.material" : "kosten.fremd")}
        </span>
        {!eintrag.weiterberechenbar && (
          <span className="rounded bg-grund px-2 py-0.5 text-xs text-leise">
            {t("kosten.nichtWeiter")}
          </span>
        )}
        <span className="ms-auto text-lg font-bold tabular-nums">{euro(eintrag.betragCent)}</span>
      </div>
      <p className="mt-1 text-sm text-leise">
        {[
          eintrag.objekt,
          eintrag.kunde,
          eintrag.abteilung,
          eintrag.menge !== null ? `${eintrag.menge} ${eintrag.einheit ?? ""}`.trim() : null,
          eintrag.lieferant,
          zeigeName ? eintrag.mitarbeiter : null,
          eintrag.notiz
        ].filter(Boolean).join(" · ")}
      </p>
      <div className="mt-2 flex flex-wrap items-center gap-4">
        {eintrag.hatBeleg && (
          beleg ? null : (
            <button
              className="text-sm text-verweis underline"
              onClick={() => void bildLaden(`/kosten/${eintrag.id}/beleg`).then(setBeleg).catch(() => {})}
            >
              {t("kosten.belegZeigen")}
            </button>
          )
        )}
        {darfLoeschen && (
          fragt ? (
            <span className="flex items-center gap-3 text-sm">
              {t("kosten.wirklichWeg")}
              <button
                className="text-red-700 underline"
                onClick={() => void api.request(`/kosten/${eintrag.id}`, { method: "DELETE" })
                  .then(onWeg).catch(() => setFragt(false))}
              >
                {t("aktion.loeschen")}
              </button>
              <button className="text-leise underline" onClick={() => setFragt(false)}>
                {t("aktion.abbrechen")}
              </button>
            </span>
          ) : (
            <button className="text-sm text-leise underline" onClick={() => setFragt(true)}>
              {t("aktion.loeschen")}
            </button>
          )
        )}
      </div>
      {beleg && <img src={beleg} alt="" className="mt-2 max-h-96 rounded" />}
    </li>
  );
}

function EintragDialog({
  objekte, abteilungen, onClose, onFertig
}: {
  objekte: Objekt[];
  abteilungen: Abteilung[];
  onClose: () => void;
  onFertig: () => void;
}) {
  const { t } = useTranslation();
  const [objektId, setObjektId] = useState(objekte[0]?.id ?? "");
  const [abteilungId, setAbteilungId] = useState("");
  const [art, setArt] = useState<"material" | "service">("material");
  /*
   * Das Datum des Belegs, nicht das der Eingabe. Eine Rechnung vom Freitag
   * wird oft erst am Montag erfasst, und im Monatsabschluss steht sie dann im
   * falschen Monat. Vorbelegt ist heute, weil das der haeufige Fall ist.
   */
  const [tag, setTag] = useState(heute());
  const [bezeichnung, setBezeichnung] = useState("");
  const [lieferant, setLieferant] = useState("");
  const [menge, setMenge] = useState("");
  const [einheit, setEinheit] = useState("");
  const [betrag, setBetrag] = useState("");
  const [weiter, setWeiter] = useState(true);
  const [notiz, setNotiz] = useState("");
  const [datei, setDatei] = useState<File | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [laeuft, setLaeuft] = useState(false);

  async function speichern() {
    setLaeuft(true);
    setFehler(null);
    try {
      // Immer als Multipart, auch ohne Foto. Zwei Wege durch dieselbe Maske
      // waeren zwei Wege, die auseinanderlaufen, sobald einer angefasst wird.
      const form = new FormData();
      form.append("objektId", objektId);
      form.append("art", art);
      form.append("bezeichnung", bezeichnung);
      form.append("datum", tag);
      form.append("betrag", betrag);
      form.append("weiterberechenbar", weiter ? "ja" : "nein");
      if (abteilungId) form.append("abteilungId", abteilungId);
      if (lieferant) form.append("lieferant", lieferant);
      if (menge) form.append("menge", menge);
      if (einheit) form.append("einheit", einheit);
      if (notiz) form.append("notiz", notiz);
      if (datei) form.append("datei", datei);
      await api.request("/kosten", { method: "POST", body: form });
      onFertig();
    } catch (e) {
      setFehler(fehlertext(e, t("fehler.speichern")));
    } finally {
      setLaeuft(false);
    }
  }

  return (
    <Dialog titel={t("kosten.neuerEintrag")} onClose={onClose}>
      <Fehler text={fehler} />

      <div className="mb-4 flex gap-2">
        <Wahlknopf an={art === "material"} onClick={() => setArt("material")}>
          {t("kosten.material")}
        </Wahlknopf>
        <Wahlknopf an={art === "service"} onClick={() => setArt("service")}>
          {t("kosten.fremd")}
        </Wahlknopf>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <Feld label={t("kosten.objekt")} breit>
          <Auswahl value={objektId} onChange={(e) => setObjektId(e.target.value)}>
            {objektAuswahl(objekte).map(({ objekt, beschriftung }) => (
              <option key={objekt.id} value={objekt.id}>{beschriftung}</option>
            ))}
          </Auswahl>
        </Feld>
        <Feld label={t("kosten.datum")} hinweis={t("kosten.datumHinweis")}>
          <Eingabe
            type="date" max={heute()}
            value={tag} onChange={(e) => setTag(e.target.value)}
          />
        </Feld>
        <Feld label={t("kosten.bezeichnung")} hinweis={t("kosten.bezeichnungHinweis")} breit>
          <Eingabe value={bezeichnung} onChange={(e) => setBezeichnung(e.target.value)} />
        </Feld>
        <Feld label={t("kosten.betrag")} hinweis={t("kosten.betragHinweis")}>
          <Eingabe
            type="number" inputMode="decimal" step="0.01" min={0}
            value={betrag} onChange={(e) => setBetrag(e.target.value)}
          />
        </Feld>
        <Feld label={t("kosten.abteilung")} hinweis={t("kosten.abteilungHinweis")}>
          <Auswahl value={abteilungId} onChange={(e) => setAbteilungId(e.target.value)}>
            <option value="">{t("kosten.ohneAbteilung")}</option>
            {abteilungen.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
          </Auswahl>
        </Feld>
        <Feld label={t("kosten.menge")}>
          <Eingabe
            type="number" inputMode="decimal" step="0.01" min={0}
            value={menge} onChange={(e) => setMenge(e.target.value)}
          />
        </Feld>
        <Feld label={t("kosten.einheit")} hinweis={t("kosten.einheitHinweis")}>
          <Eingabe value={einheit} onChange={(e) => setEinheit(e.target.value)} />
        </Feld>
        <Feld label={t("kosten.lieferant")}>
          <Eingabe value={lieferant} onChange={(e) => setLieferant(e.target.value)} />
        </Feld>
        <Feld label={t("kosten.notiz")}>
          <Eingabe value={notiz} onChange={(e) => setNotiz(e.target.value)} />
        </Feld>
        <Feld label={t("kosten.beleg")} hinweis={t("kosten.belegHinweis")} breit>
          <Fotofeld datei={datei} onDatei={setDatei} beschriftung={t("foto.belegAufnehmen")} />
        </Feld>
      </div>

      <label className="mt-4 flex items-center gap-3 text-sm font-semibold">
        <input type="checkbox" checked={weiter} onChange={(e) => setWeiter(e.target.checked)} />
        {t("kosten.weiterberechenbar")}
      </label>

      <div className="mt-5 flex gap-2">
        <Knopf disabled={laeuft || !objektId || !bezeichnung || !betrag} onClick={() => void speichern()}>
          {t("aktion.speichern")}
        </Knopf>
        <Knopf art="neben" onClick={onClose}>{t("aktion.abbrechen")}</Knopf>
      </div>
    </Dialog>
  );
}

function Wahlknopf({
  an, onClick, children
}: { an: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded px-4 py-2 text-sm font-semibold transition ${
        an ? "bg-marke text-white" : "bg-grund text-tinte hover:bg-grund/70"
      }`}
    >
      {children}
    </button>
  );
}

function Dialog({
  titel, children, onClose
}: { titel: string; children: ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-full w-full max-w-2xl overflow-y-auto rounded-card bg-white p-6">
        <h2 className="mb-4 text-lg font-bold">{titel}</h2>
        {children}
        <button className="sr-only" onClick={onClose}>schliessen</button>
      </div>
    </div>
  );
}

export function euro(cent: number): string {
  return `${(cent / 100).toFixed(2)} €`;
}

function datum(tag: string): string {
  return new Date(`${tag}T12:00:00Z`).toLocaleDateString(sprache(), {
    day: "2-digit", month: "2-digit", year: "2-digit"
  });
}
